/**
 * In-memory backend for the guided demo tour.
 *
 * `createDemoMockTransport` returns a transport for
 * `setMockTransport` (``shared/http/client.js``): every request the wizard
 * pages issue while the demo is active is answered from a handler map keyed
 * like the Playwright fixtures (``'POST dataset-records'``), backed by the
 * IMDb fixtures in ``demoFixtures.js`` and a small mutable state (the demo
 * usecase, the two "uploaded" files and their rows). Nothing leaves the
 * browser and nothing is persisted; the state dies with the transport.
 *
 * Endpoints the demo never needs still have to be answered, because the
 * pages fetch them on mount (Data Editor counts, Train hardware settings,
 * …). Anything *not* in the map is passed through (``undefined``) and
 * logged in dev: the app shell keeps talking to the real backend
 * (``auth/me``, ``llm_usecases``, preferences) — swallowing those with an
 * empty 200 would corrupt the signed-in session on a demo page refresh.
 */

import {
    DEMO_BENCHMARK_NAME,
    DEMO_BENCHMARK_ROWS,
    DEMO_DATASET_NAME,
    DEMO_DATASET_ROWS,
    DEMO_DISTILLATION_EVENT_ID,
    DEMO_EXAMPLE_COLUMN,
    DEMO_LABELS,
    DEMO_LABEL_COLUMN,
    DEMO_TEACHER_PROVIDER,
    DEMO_USECASE_ID,
} from './demoFixtures';

/** Scheme of the signed upload URL the mock hands out; the PUT comes straight back to us. */
export const DEMO_UPLOAD_URL_PREFIX = 'demo://upload/';

const MAX_DATASET_NAME_LENGTH = 40;

/** Same clone-name rule the real upload flow predicts (``datasetUploadFlow.js``). */
export function demoCloneName(datasetName, usecaseId = DEMO_USECASE_ID) {
    return `${String(datasetName).slice(0, MAX_DATASET_NAME_LENGTH)}__t_${String(usecaseId).slice(-6)}`;
}

const baseName = (fileName) => String(fileName || '').replace(/\.[^.]+$/, '');

const formValue = (body, key) => {
    if (!body || typeof body.get !== 'function') return body?.[key];
    const value = body.get(key);
    return value == null ? undefined : value;
};

const formBool = (body, key) => {
    const value = formValue(body, key);
    return value === true || value === 'true' || value === '1';
};

const parseJson = (text, fallback) => {
    if (text == null || text === '') return fallback;
    if (typeof text !== 'string') return text;
    try {
        return JSON.parse(text);
    } catch {
        return fallback;
    }
};

/** Row ids are stable so the Data Editor can key its table and the update handlers can find them. */
function buildRecords(rows, { datasetName, isBenchmark, clientId }) {
    return rows.map((row, index) => ({
        _id: `demo-${isBenchmark ? 'bm' : 'ds'}-${index + 1}`,
        client_id: clientId,
        dataset: datasetName,
        is_benchmark: isBenchmark,
        request_example: row[DEMO_EXAMPLE_COLUMN],
        response: { labels: [row[DEMO_LABEL_COLUMN]] },
        data_creation_type: 'commercial_model',
        is_document_reviewed: false,
        is_in_dataset: true,
        extra_params: {},
    }));
}

function quality(n) {
    return {
        num_records: n,
        total_num_records: n,
        processed_num_records: n,
        num_high_quality: n,
        num_medium_quality: 0,
        num_low_quality: 0,
    };
}

function labelCounts(records) {
    const counts = {};
    DEMO_LABELS.forEach((label) => {
        counts[label] = { total: 0, validated: 0 };
    });
    records.forEach((record) => {
        (record.response?.labels || []).forEach((label) => {
            if (!counts[label]) counts[label] = { total: 0, validated: 0 };
            counts[label].total += 1;
            if (record.is_document_reviewed) counts[label].validated += 1;
        });
    });
    return counts;
}

function sortRecords(records, sortField, sortOrder) {
    if (!sortField) return records;
    const dir = String(sortOrder || '').toLowerCase().startsWith('desc') ? -1 : 1;
    return [...records].sort((a, b) => {
        const av = a[sortField] ?? '';
        const bv = b[sortField] ?? '';
        if (av === bv) return 0;
        return av > bv ? dir : -dir;
    });
}

function filterRecords(records, filters) {
    if (!filters || typeof filters !== 'object') return records;
    let result = records;
    if (Array.isArray(filters._id)) {
        // Improvement Mode "View": the ranked review queue's record ids.
        result = result.filter((r) => filters._id.includes(r._id));
    } else if (filters._id) {
        result = result.filter((r) => r._id === filters._id);
    }
    if (Array.isArray(filters.labels) && filters.labels.length) {
        result = result.filter((r) => (r.response?.labels || []).some((l) => filters.labels.includes(l)));
    }
    if (typeof filters.is_document_reviewed === 'boolean') {
        result = result.filter((r) => r.is_document_reviewed === filters.is_document_reviewed);
    }
    if (filters.request_example) {
        const needle = String(filters.request_example).toLowerCase();
        result = result.filter((r) => String(r.request_example).toLowerCase().includes(needle));
    }
    return result;
}

/**
 * @param {object} options
 * @param {() => object} options.getUsecase  latest demo usecase
 * @param {(patch: object) => void} options.setUsecase  merge a patch into the demo usecase
 * @param {(message: string, request: object) => void} [options.onUnhandled]  dev logging hook
 */
export function createDemoMockTransport({ getUsecase, setUsecase, onUnhandled } = {}) {
    const usecaseOf = () => getUsecase?.() || {};
    const clientIdOf = () => usecaseOf().client_id;
    const patchUsecase = (patch) => setUsecase?.(patch);

    /** key: clone name → { records, isBenchmark, info } */
    const files = new Map();
    /** object_key → { fileName, isBenchmark } for the two-phase upload handshake */
    const pendingUploads = new Map();
    let uploadCounter = 0;
    const handledSuffixes = [];

    const fileFor = (isBenchmark) =>
        [...files.values()].find((f) => f.isBenchmark === Boolean(isBenchmark)) || null;
    const recordsFor = (isBenchmark) => fileFor(isBenchmark)?.records || [];

    const infoDoc = (file) => ({
        dataset: file.name,
        is_benchmark: file.isBenchmark,
        is_manual_model_predictions: false,
        data_creation_type: file.dataCreationType,
        column_mappings: {
            example_column_name: DEMO_EXAMPLE_COLUMN,
            label_column_name: DEMO_LABEL_COLUMN,
            label_mappings: file.labelMappings,
            import_without_labels: false,
        },
        file_column_names: [DEMO_EXAMPLE_COLUMN, DEMO_LABEL_COLUMN],
        extra_params_keys: [],
        num_records: file.records.length,
        total_num_records: file.records.length,
        request_example_summary_data: true,
        llm_usecase_ids: [DEMO_USECASE_ID],
    });

    const metaData = () => {
        const dataset = fileFor(false);
        const benchmark = fileFor(true);
        return {
            dataset: quality(dataset?.records.length || 0),
            benchmark: quality(benchmark?.records.length || 0),
            available_extra_params_keys: {},
            cross_reference_fields: {},
            optune_specific_info: {},
            datasets_info: [...files.values()].map(infoDoc),
        };
    };

    const attachFile = ({ fileName, isBenchmark, dataCreationType, labelMappings }) => {
        const name = demoCloneName(baseName(fileName) || (isBenchmark ? DEMO_BENCHMARK_NAME : DEMO_DATASET_NAME));
        const rows = isBenchmark ? DEMO_BENCHMARK_ROWS : DEMO_DATASET_ROWS;
        files.set(name, {
            name,
            isBenchmark: Boolean(isBenchmark),
            dataCreationType: dataCreationType || 'commercial_model',
            labelMappings: labelMappings || {},
            records: buildRecords(rows, { datasetName: name, isBenchmark, clientId: clientIdOf() }),
        });
        const current = usecaseOf();
        const key = isBenchmark ? 'benchmarks' : 'datasets';
        const existing = Array.isArray(current[key]) ? current[key] : [];
        patchUsecase({
            [key]: existing.includes(name) ? existing : [...existing, name],
            ...(current.status === 'Waiting for Data' && !isBenchmark ? { status: 'Data Uploaded' } : {}),
        });
        return name;
    };

    const findRecord = (body) => {
        const isBenchmark = Boolean(body?.is_benchmark);
        const records = recordsFor(isBenchmark);
        const byId = body?.record_id ? records.find((r) => r._id === body.record_id) : null;
        return byId || records.find((r) => r.request_example === body?.request_example) || null;
    };

    const datasetRecords = (body) => {
        const isBenchmark = Boolean(body?.is_benchmark);
        const all = recordsFor(isBenchmark);
        const filtered = sortRecords(filterRecords(all, body?.filters), body?.sort_field, body?.sort_order);
        const first = Number(body?.first_index) || 0;
        const count = Number(body?.num_records) || filtered.length;
        return {
            records: filtered.slice(first, first + count),
            filtered_count: filtered.length,
            total_count: all.length,
            filtered_labels: [...DEMO_LABELS],
        };
    };

    const rankedExamples = (body) => {
        const records = recordsFor(body?.is_benchmark);
        const label = body?.label;
        const matching = label
            ? records.filter((r) => (r.response?.labels || []).includes(label))
            : records;
        const k = Number(body?.k) || matching.length;
        return matching.slice(0, k).map((r) => ({ record_id: r._id, score: 1 }));
    };

    const ok = (body = {}) => ({ status: 200, body });

    const handlers = {
        // --- task lifecycle -------------------------------------------------
        'POST create_llm_usecase': () => ok({ success: true, usecase_id: DEMO_USECASE_ID, _id: DEMO_USECASE_ID }),
        'POST update_llm_usecase': ({ body }) => {
            const { usecase_id: _usecaseId, client_id: _clientId, ...patch } = body || {};
            patchUsecase(patch);
            return ok({ success: true });
        },
        'POST task-wizard-step': () => ok({ success: true }),
        'POST llm_usecase_info': () => ok({ llm_usecase: usecaseOf() }),
        'POST update_comparison_mode': ({ body }) => {
            patchUsecase({ comparison_mode: body?.comparison_mode ?? null });
            return ok({ success: true });
        },
        'POST update_comparison_mode_option': () => ok({ success: true }),

        // --- data collection ------------------------------------------------
        'POST datasets-and-benchmarks': () => ok({ datasets: [], benchmarks: [], manual_model_predictions: [] }),
        'POST datasets': () => ok({ datasets: [], benchmarks: [], manual_model_predictions: [] }),
        'POST is-dataset-exists': () => ok({ result: false, exists: false }),
        'POST get-file-columns': ({ body }) => {
            // JSON body: { filename, client_id, file_content, file_type }
            const isBenchmark = /benchmark/i.test(String(formValue(body, 'filename') || ''));
            const rows = isBenchmark ? DEMO_BENCHMARK_ROWS : DEMO_DATASET_ROWS;
            return ok({
                success: true,
                columns: [DEMO_EXAMPLE_COLUMN, DEMO_LABEL_COLUMN],
                sample_rows: rows.slice(0, 5).map((row) => ({ ...row })),
            });
        },
        'POST get-file-labels-upload': () => ok({
            success: true,
            labels: [...DEMO_LABELS],
            label_examples: Object.fromEntries(
                DEMO_LABELS.map((label) => [
                    label,
                    DEMO_DATASET_ROWS.filter((row) => row[DEMO_LABEL_COLUMN] === label)
                        .slice(0, 2)
                        .map((row) => row[DEMO_EXAMPLE_COLUMN]),
                ])
            ),
        }),
        'POST get-file-labels': () => handlers['POST get-file-labels-upload'](),
        'POST create-dataset-upload-url': ({ body }) => {
            uploadCounter += 1;
            const fileName = formValue(body, 'filename') || formValue(body, 'upload_filename') || 'upload.csv';
            const objectKey = `demo/${uploadCounter}/${fileName}`;
            pendingUploads.set(objectKey, { fileName, isBenchmark: formBool(body, 'is_benchmark') });
            return ok({
                upload_url: `${DEMO_UPLOAD_URL_PREFIX}${encodeURIComponent(objectKey)}`,
                content_type: formValue(body, 'content_type') || 'text/csv',
                object_key: objectKey,
                status_id: `demo-upload-status-${uploadCounter}`,
            });
        },
        'POST complete-dataset-upload': ({ body }) => {
            const objectKey = formValue(body, 'object_key');
            const pending = pendingUploads.get(objectKey) || {};
            const fileName = formValue(body, 'upload_filename') || pending.fileName;
            const isBenchmark = formBool(body, 'is_benchmark') || Boolean(pending.isBenchmark);
            const name = attachFile({
                fileName,
                isBenchmark,
                dataCreationType: formValue(body, 'data_creation_type'),
                labelMappings: parseJson(formValue(body, 'label_mappings'), {}),
            });
            pendingUploads.delete(objectKey);
            return ok({ success: true, dataset_name: name, status_id: formValue(body, 'status_id') });
        },
        'POST apply-label-column-to-dataset': () => ok({ success: true }),
        'POST apply-example-field-to-dataset': () => ok({ success: true }),
        'POST clear-label-column-from-dataset': () => ok({ success: true }),
        'POST datasets-and-benchmarks-meta-data': () => ok(metaData()),
        'POST dataset-sample-record': ({ body }) => {
            const records = recordsFor(body?.is_benchmark);
            const limit = Math.max(1, Number(body?.limit) || 1);
            const samples = records.slice(0, limit).map((r) => ({
                [DEMO_EXAMPLE_COLUMN]: r.request_example,
                [DEMO_LABEL_COLUMN]: r.response.labels[0],
            }));
            return ok({
                sample: samples[0] || null,
                samples,
                distinct_labels: [...DEMO_LABELS],
                distinct_relations: [],
                has_empty_label_values: false,
                has_empty_trajectory_values: false,
            });
        },
        'POST dataset-records/extra-param-values': () => ok({ values: [] }),
        'POST dataset-records/tool-call-names': () => ok({ values: [] }),

        // --- model setup ----------------------------------------------------
        // `useRecommendedProvider` reads `recommended_provider.provider`; Open AI
        // being recommended is what lists it (and gpt-5) first in the model dropdown.
        'POST get_organization_settings': () => ok({ recommended_provider: { provider: DEMO_TEACHER_PROVIDER } }),
        'POST manual-model-predictions-info': () => ok({ datasets_info: [] }),
        'POST usecase-models': () => ok({ models: [] }),
        'POST save_teacher_and_prompt': ({ body }) => {
            patchUsecase({
                prompt: body?.prompt ?? null,
                teacher_model: body?.teacher || null,
                teacher_provider: body?.provider || DEMO_TEACHER_PROVIDER,
                teacher_model_display: body?.teacher_display || body?.teacher || null,
                teacher_system_prompt: body?.teacher_system_prompt || null,
                teacher_schema: body?.teacher_schema || null,
            });
            return ok({ success: true });
        },
        'POST validate-code': ({ body }) => {
            patchUsecase({ parser_function: body?.code ?? null });
            return ok({ is_valid: true, parsed_result: DEMO_LABELS[0] });
        },
        'POST save_evaluation_normalization': () => ok({ success: true }),
        'POST save_generative_settings': () => ok({ success: true }),
        'POST raw-responses': ({ body }) => {
            const record = recordsFor(false)[0] || recordsFor(true)[0];
            const example = record?.request_example || DEMO_DATASET_ROWS[0][DEMO_EXAMPLE_COLUMN];
            const label = record?.response?.labels?.[0] || DEMO_LABELS[0];
            return ok({
                success: true,
                data: [
                    {
                        _id: record?._id || 'demo-ds-1',
                        request_example: example,
                        value: label,
                        response: { labels: [label], extra_params: {} },
                        teacher_model: body?.teacher_model,
                    },
                ],
            });
        },
        'POST raw-responses/batch-status': () => ok({ status: 'completed', results: [] }),
        'POST arm-and-start-label-generation': () => ok({
            started_datasets: [],
            pending_ingest_datasets: [],
            already_triggered_datasets: [],
            skipped_no_unlabeled_datasets: [...files.keys()],
        }),

        // --- data editor ----------------------------------------------------
        'POST dataset-records': ({ body }) => ok(datasetRecords(body)),
        'POST dataset-records-counts': () => ok({
            active_dataset_count: recordsFor(false).length,
            active_benchmark_count: recordsFor(true).length,
            archived_dataset_count: 0,
            archived_benchmark_count: 0,
        }),
        'POST get-labels': () => ok({ label_names: [...DEMO_LABELS], entity_names: [], relation_names: [] }),
        'POST get-relations-label-names': () => ok([]),
        'POST get-display-names': () => ok({}),
        'POST get-label-counts': ({ body }) => {
            const records = recordsFor(body?.is_benchmark);
            const reviewed = records.filter((r) => r.is_document_reviewed).length;
            return ok({
                success: true,
                label_counts: labelCounts(records),
                entity_counts: {},
                relation_counts: {},
                record_counts: { total: records.length, reviewed },
                // Enables the per-label "View" buttons (the "Focus on one
                // label" mark is clicked, not skipped) — the review queue is
                // then `get-top-ranked-examples`, answered below.
                rank_score_initialized: true,
                unlabeled_count: { total: 0 },
                none_selected_count: { total: 0 },
            });
        },
        'POST single-example/update-response': ({ body }) => {
            const record = findRecord(body);
            if (!record) return ok({ success: false, message: 'Record not found' });
            record.response = body?.new_response ?? record.response;
            if (body?.new_request_example) record.request_example = body.new_request_example;
            record.is_document_reviewed = body?.is_document_reviewed ?? true;
            return ok({ success: true, new_record: { ...record } });
        },
        'POST single-example/update-example': ({ body }) => {
            const record = findRecord(body);
            if (!record) return ok({ success: false, message: 'Record not found' });
            record.request_example = body?.new_request_example ?? body?.new_example ?? record.request_example;
            return ok({ success: true, new_record: { ...record } });
        },
        'POST single-example/update-is-document-reviewed': ({ body }) => {
            const record = findRecord(body);
            if (!record) return ok({ success: false, message: 'Record not found' });
            record.is_document_reviewed = Boolean(body?.is_document_reviewed);
            return ok({ success: true, new_record: { ...record } });
        },
        'POST set-is-in-db': ({ body }) => {
            const record = findRecord(body);
            if (record) record.is_in_dataset = body?.is_in_db ?? body?.is_in_dataset ?? record.is_in_dataset;
            return ok({ success: true });
        },
        'POST bulk-operations': () => ok({ success: true, updated: 0 }),
        'POST raw-records-stats': () => ok({ total: 0, with_response: 0, without_response: 0 }),
        'POST raw-records': () => ok({ records: [], total: 0 }),
        // Improvement Mode "View" (`rank_score_initialized` above): the
        // review queue is the label's rows, so the table keeps something to
        // "Review and correct".
        'POST get-top-ranked-examples': ({ body }) => ok({ success: true, examples: rankedExamples(body) }),
        'POST get-similar-validated-examples': ({ body }) => ok({ success: true, examples: rankedExamples(body) }),
        'POST recalculate_score': () => ok({ success: true }),

        // --- train ----------------------------------------------------------
        'POST get_user_settings': () => ok({ hardware_device: 'gpu' }),
        'POST trainings': () => ok({ trainings: [] }),
        // "Finish & Create Model" succeeds without training anything: the
        // Train step sees the task move to `distillation_in_progress` and
        // ends the demo on the "You're all set" modal.
        'POST create_distillation_event': ({ body }) => {
            patchUsecase({ version: body?.version ?? (usecaseOf().version || 0) + 1 });
            return ok({ distillation_event_id: DEMO_DISTILLATION_EVENT_ID });
        },
        'POST create_training': () => ok({ success: true }),
    };

    /** GET endpoints carry ids / query strings in the path, so they match by prefix. */
    const getPrefixes = [
        ['base-models', ({ suffix }) => {
            const query = suffix.split('?')[1] || '';
            const taskType = new URLSearchParams(query).get('task_type') || usecaseOf().task_type || null;
            return ok({ task_type: taskType, models: [] });
        }],
        ['ai-generation/sessions/', () => ok({ sessions: [] })],
        ['ai-generation/status/', () => ok({ status: 'idle', active: false })],
        ['ai-generation/dataset-name-suggestion/', () => ok({ default_name: DEMO_DATASET_NAME, existing_names: [] })],
        ['retrieval-agent/sessions/', () => ok({ sessions: [] })],
        ['upload-status/', () => ok({ status: 'completed', progress: 100 })],
    ];

    const transport = async (request) => {
        const { method = 'POST', suffix = '', url = '' } = request || {};
        const path = String(suffix).replace(/^\/+/, '');

        if (method === 'PUT' && String(url).startsWith(DEMO_UPLOAD_URL_PREFIX)) {
            handledSuffixes.push(`PUT ${path}`);
            return ok({});
        }

        const key = `${method} ${path}`;
        const exact = handlers[key];
        if (exact) {
            handledSuffixes.push(key);
            return exact(request);
        }
        if (method === 'GET') {
            const match = getPrefixes.find(([prefix]) => path.startsWith(prefix));
            if (match) {
                handledSuffixes.push(key);
                return match[1](request);
            }
        }
        onUnhandled?.(`[walkthrough demo] no mock for ${key}; passing through to the backend`, request);
        return undefined;
    };

    /** Test / debug surface; not used by the wizard. */
    transport.state = {
        files,
        handled: handledSuffixes,
        recordsFor,
        reset() {
            files.clear();
            pendingUploads.clear();
            handledSuffixes.length = 0;
            uploadCounter = 0;
        },
    };
    transport.handlerKeys = Object.keys(handlers);
    return transport;
}

/**
 * Demo uploads never reach a data_processor, so a card that enters the
 * "processing" phase finishes on the spot; otherwise Data Overview would spin
 * forever waiting for WebSocket progress events that never come.
 */
export function settleDemoProcessing(progress) {
    if (!progress || typeof progress !== 'object') return progress;
    let changed = false;
    const next = {};
    Object.entries(progress).forEach(([key, item]) => {
        if (item && item.phase === 'processing' && !item.isCompleted && !item.isError) {
            changed = true;
            next[key] = {
                ...item,
                phase: 'complete',
                progress: 100,
                uploadProgress: 100,
                processingProgress: 100,
                isCompleted: true,
                message: 'Completed',
            };
        } else {
            next[key] = item;
        }
    });
    return changed ? next : progress;
}
